import { useCallback, useEffect, useRef } from "react"

import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { REPORT_AUTOSAVE_DEBOUNCE_MS } from "../redux/reportCanvas.constants.ts"
import { saveReportCanvas } from "../redux/reportCanvas.thunks.ts"
import type { ReportDefinitionV1 } from "../types/canvas.types.ts"

interface SaveNowResult {
  ok: boolean
  saved: boolean
}

interface UseReportCanvasAutosaveReturn {
  saveStatus: "idle" | "dirty" | "saving" | "saved" | "error"
  saveError: string | null
  retry: () => void
  saveNow: () => Promise<SaveNowResult>
  cancel: () => void
}

interface PendingSaveSnapshot {
  reportId: string
  definition: ReportDefinitionV1
  dirty: boolean
  dirtySeq: number
}

export function useReportCanvasAutosave(
  reportId: string | undefined,
  readOnly = false
): UseReportCanvasAutosaveReturn {
  const dispatch = useAppDispatch()
  const saveStatus = useAppSelector((s) => s.reportCanvas.saveStatus)
  const saveError = useAppSelector((s) => s.reportCanvas.saveError)
  const dirtySeq = useAppSelector((s) => s.reportCanvas.dirtySeq)
  const savedSeq = useAppSelector((s) => s.reportCanvas.savedSeq)
  const definition = useAppSelector((s) => s.reportCanvas.definition)

  const timerRef = useRef(0)
  const pendingRef = useRef(false)
  const snapshotRef = useRef<PendingSaveSnapshot | null>(null)
  const saveStatusRef = useRef(saveStatus)
  const lastDispatchedSeqRef = useRef(savedSeq)
  const activeReportIdRef = useRef(reportId)
  const cancelledReportIdRef = useRef<string | undefined>(undefined)
  const readOnlyRef = useRef(readOnly)
  const strandedSaveRef = useRef<PendingSaveSnapshot | null>(null)

  saveStatusRef.current = saveStatus
  readOnlyRef.current = readOnly
  if (activeReportIdRef.current !== reportId) {
    // Switching reports in the rail resets the canvas without unmounting, which
    // would drop an edit still inside the debounce with no request and no
    // signal to the user. Hand it to the effect below instead.
    if (
      !readOnly &&
      pendingRef.current &&
      snapshotRef.current?.dirty &&
      cancelledReportIdRef.current !== snapshotRef.current.reportId
    )
      strandedSaveRef.current = snapshotRef.current
    activeReportIdRef.current = reportId
    lastDispatchedSeqRef.current = savedSeq
    pendingRef.current = false
    snapshotRef.current = null
    cancelledReportIdRef.current = undefined
  }
  // A re-hydrate rewinds dirtySeq; a stranded watermark would mark no later edit
  // as pending, silently stopping autosave.
  if (lastDispatchedSeqRef.current > dirtySeq) {
    lastDispatchedSeqRef.current = dirtySeq
  }
  if (reportId && definition) {
    snapshotRef.current = {
      reportId,
      definition,
      dirty: dirtySeq > savedSeq,
      dirtySeq
    }
    if (!readOnly && dirtySeq > lastDispatchedSeqRef.current)
      pendingRef.current = true
  }

  const dispatchSave = useCallback(
    (force = false) => {
      if (readOnlyRef.current) return
      const snapshot = snapshotRef.current
      if (!snapshot || (!force && !pendingRef.current) || !snapshot.dirty)
        return
      if (cancelledReportIdRef.current === snapshot.reportId) return
      if (saveStatusRef.current === "saving") return
      pendingRef.current = false
      lastDispatchedSeqRef.current = snapshot.dirtySeq
      void dispatch(saveReportCanvas(snapshot))
    },
    [dispatch]
  )

  const flush = useCallback(() => {
    window.clearTimeout(timerRef.current)
    if (!pendingRef.current) return
    dispatchSave()
  }, [dispatchSave])

  const retry = useCallback(() => {
    window.clearTimeout(timerRef.current)
    dispatchSave(true)
  }, [dispatchSave])

  // Abandon any pending/in-flight autosave for the active report — used before a
  // delete so the unmount flush can't PATCH a report that no longer exists.
  const cancel = useCallback(() => {
    window.clearTimeout(timerRef.current)
    pendingRef.current = false
    cancelledReportIdRef.current = activeReportIdRef.current
  }, [])

  // Explicit Save: flush any pending edit immediately and resolve once the
  // PATCH settles, so callers can surface success/failure feedback. Autosave
  // stays active — this only short-circuits the debounce.
  const saveNow = useCallback(async (): Promise<SaveNowResult> => {
    window.clearTimeout(timerRef.current)
    if (readOnlyRef.current) return { ok: true, saved: false }
    const snapshot = snapshotRef.current
    if (!snapshot || !snapshot.dirty) return { ok: true, saved: false }
    if (cancelledReportIdRef.current === snapshot.reportId)
      return { ok: true, saved: false }
    if (saveStatusRef.current === "saving") return { ok: true, saved: false }
    pendingRef.current = false
    lastDispatchedSeqRef.current = snapshot.dirtySeq
    const action = await dispatch(saveReportCanvas(snapshot))
    return { ok: saveReportCanvas.fulfilled.match(action), saved: true }
  }, [dispatch])

  useEffect(() => {
    const stranded = strandedSaveRef.current
    if (!stranded) return
    strandedSaveRef.current = null
    void dispatch(saveReportCanvas(stranded))
  })

  const wasSavingRef = useRef(false)
  useEffect(() => {
    const wasSaving = wasSavingRef.current
    wasSavingRef.current = saveStatus === "saving"
    window.clearTimeout(timerRef.current)
    if (
      saveStatus === "saving" ||
      saveStatus !== "dirty" ||
      !reportId ||
      !definition ||
      !pendingRef.current
    )
      return
    if (wasSaving) {
      flush()
      return
    }
    timerRef.current = window.setTimeout(flush, REPORT_AUTOSAVE_DEBOUNCE_MS)
    return () => window.clearTimeout(timerRef.current)
  }, [dirtySeq, saveStatus, reportId, definition, flush])

  useEffect(() => () => flush(), [flush])

  return { saveStatus, saveError, retry, saveNow, cancel }
}
